import React, { PropsWithChildren } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import CompanyProfileInvitationService
	from '@app/model/company_profile_invitation/service/CompanyProfileInvitationService';
import FormPlaceholder from '@app/component/placeholder/FormPlaceholder';
import ErrorPageNotFound from '@app/site/system/page/ErrorPageNotFound';
import { useForm } from 'react-hook-form';
import { Switch } from '@headlessui/react';
import FormSwitch from '@app/component/form/input/switch/FormSwitch';
import FormButton from '@app/component/form/button/FormButton';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import AcceptCompanyProfileInvitationInput
	from '@app/model/company_profile_invitation/input/AcceptCompanyProfileInvitationInput';
import { useTranslation } from '@app/component/translation/Translation';
import FormInputPassword from '@app/component/form/input/text/FormInputPassword';


const AcceptCompanyProfileInvitationPage = React.memo(() => {
	const { key } = useParams();
	const navigate = useNavigate();
	const form = useForm();
	const toaster = useToaster();
	const { t } = useTranslation();
	const hasAcceptedTerms = form.watch('acceptTerms');

	const acceptInvitationMutation = useMutation({
		mutationFn: CompanyProfileInvitationService.acceptCompanyProfileInvitation,
		onSuccess: () => {
			navigate('/sign-in');
			toaster.success('Invitation', 'Invitation accepted. Please sign in!');
		}, onError: (e: string) => {
			toaster.error('Accept Invitation', t(e));
		},
	});

	if (!!key) {

		const { data: invitation, isLoading, isSuccess, isError } = useQuery({
			queryFn: () => CompanyProfileInvitationService.findActiveCompanyProfileInvitationByKey(key),
			queryKey: ['invitation'],
			retry: false,
		});
		if (isLoading)
			return <Wrapper><FormPlaceholder /></Wrapper>;

		if (isSuccess) {
			form.setValue('key', invitation.key);

			const onSubmit = (data: AcceptCompanyProfileInvitationInput) => {
				acceptInvitationMutation.mutate(data);
			};

			return <Wrapper>
				<div className="space-y-6">
					<div>
						<div className="flex justify-between"><label htmlFor="input-normal" className="label">Email
							address</label></div>
						<div className="mt-0.5">
							<input type="email" className="input input-disabled" disabled={true}
										 value={invitation.email} />
						</div>
					</div>
					<FormInputPassword name="password" form={form} label="Password"
												 placeholder="Insert your password" />
					<FormInputPassword name="confirmPassword" form={form} label="Confirm Password"
												 placeholder="Confirm your password"
												 validation={{
													 check: (value) => {
														 if (value !== form.getValues('password')) return 'Passwords do not match';
														 return true;
													 },
												 }}/>
				</div>
				<div className="mt-8">
					<Switch.Group as="div" className="flex gap-x-4 sm:col-span-2 mt-3">
						<div className="flex h-6 items-center">
							<FormSwitch name="acceptTerms" form={form}
													label="Agree to the policy" />
							<Switch.Label className="text-sm leading-6 text-slate-600 ml-2">
								By selecting this, you agree to our{' '}
								<a href="/sdsd"
									 className="font-semibold text-blue-600 hover:text-blue-500">privacy&nbsp;policy</a>.
							</Switch.Label>
						</div>
					</Switch.Group>
					<FormButton type="submit" state="primary" extraClassName="flex w-full mt-4"
											isLoading={acceptInvitationMutation.isPending}
											disabled={!hasAcceptedTerms} onClick={form.handleSubmit(onSubmit)}>
						Sign up</FormButton>
				</div>
			</Wrapper>;
		}
		if (isError)
			navigate('/sign-in');
	} else navigate('/sign-in');


	return <ErrorPageNotFound />;
});

const Wrapper: React.FunctionComponent<PropsWithChildren> = React.memo(({ children }) => {
	return <div className="min-h-full bg-white">
		<div className="flex lg:h-dvh min-w-max content-center justify-center flex-1">
			<div className="flex flex-1 flex-col justify-center px-6 py-12">
				<div className="mx-auto w-full max-w-sm lg:w-96">
					<div>
						<img
							className="h-10 w-auto"
							src="/assets/img/brand/logo-icon.png"
							alt="RoomPulse"
						/>
						<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
							Activate account
						</h2>
					</div>

					<div className="mt-10">
						{children}
					</div>
				</div>
			</div>
			<div className="relative hidden flex-1 lg:block">
				<img
					className="absolute inset-0 h-full w-full object-cover"
					src="https://images.unsplash.com/photo-1629993252851-9dba13da01e8?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					alt=""
				/>
			</div>
		</div>
	</div>;
});

export default AcceptCompanyProfileInvitationPage;